import { ApolloError } from '@apollo/client'

import { useLazyBuyAgain } from '../../graphQL/queries/buyAgain'
import {
  IBuyAgainResponse,
  IBuyAgainVariables,
} from '../../graphQL/queries/buyAgain/types'
import {
  setBuyAgainItems,
  setBuyAgainStatus,
} from '../../redux/slices/recommendations'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import {
  handleRecommendationFetchError,
  mergeCartQuantitiesIntoRecommendationItems,
  skipIfMissingIdentifiers,
} from './recommendationHookHelpers'

const FUNCTION_NAME = 'useBuyAgain'

/**
 * Hook that fetches the `buyAgain` recommendations carousel data and writes
 * it into the matching Redux slice. Triggered from the EventManager via the
 * `templateActions.buyAgain.fetchProducts` template action.
 */
export const useBuyAgain = () => {
  const dispatch = useAppDispatch()
  const config = useAppSelector((state) => state.defaultSlice.config)
  const cartItems = useAppSelector((state) => state.cartSlice.items)
  const cartGroups = useAppSelector((state) => state.cartSlice.groups)
  const cartActivePromotions = useAppSelector(
    (state) => state.cartSlice.activePromotions,
  )

  const onCompleted = ({ buyAgain }: IBuyAgainResponse) => {
    // Apollo invokes onCompleted on a detached microtask whose throws are
    // silently swallowed in production builds. Wrap the merge + dispatch so
    // any throw surfaces as ERROR + Datadog instead of leaving the slice
    // permanently in LOADING.
    try {
      const merged = mergeCartQuantitiesIntoRecommendationItems(
        buyAgain.items,
        {
          items: cartItems,
          groups: cartGroups ?? [],
          activePromotions: cartActivePromotions,
        },
        config,
      )
      dispatch(setBuyAgainItems(merged))
      dispatch(setBuyAgainStatus('OK'))
    } catch (exception) {
      dispatch(setBuyAgainStatus('ERROR'))
      handleRecommendationFetchError(
        exception,
        FUNCTION_NAME,
        'buyAgain onCompleted threw during cart-quantity merge',
      )
    }
  }

  const onError = (error: ApolloError) => {
    dispatch(setBuyAgainStatus('ERROR'))
    handleRecommendationFetchError(error, FUNCTION_NAME, 'buyAgain fetch failed')
  }

  const [getBuyAgain] = useLazyBuyAgain({ onCompleted, onError })

  const fetchBuyAgain = async (variables: IBuyAgainVariables) => {
    if (skipIfMissingIdentifiers(variables, FUNCTION_NAME, 'buyAgain')) return
    dispatch(setBuyAgainStatus('LOADING'))
    try {
      await getBuyAgain({ variables })
    } catch (exception) {
      dispatch(setBuyAgainStatus('ERROR'))
      handleRecommendationFetchError(
        exception,
        FUNCTION_NAME,
        'buyAgain fetch threw synchronously',
      )
    }
  }

  return fetchBuyAgain
}
